/**
 * HistogramPreview Component
 * Handles rendering for histograms
 */

import { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import {
  resolveXAxisLabels,
  makeTickComponent,
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  niceDomain,
  getCompactTickFormatter,
} from '../../utils/chartLayout';
import { getColors } from '../../charts/shared/palettes';

function HistogramPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();

  const rawData = (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0)
    ? transformData(userData, mapping, chartType.id)
    : chartType.sampleData;

  // Calculate histogram bins
  const histogramData = useMemo(() => {
    const numBins = config.numBins || 12;
    const min = Math.min(...rawData);
    const max = Math.max(...rawData);
    const binWidth = (max - min) / numBins;

    // Initialize bins
    const bins = Array.from({ length: numBins }, (_, i) => ({
      binStart: min + i * binWidth,
      binEnd: min + (i + 1) * binWidth,
      count: 0,
      label: `${Math.round(min + i * binWidth)}-${Math.round(min + (i + 1) * binWidth)}`,
    }));

    // Count values in each bin
    rawData.forEach(value => {
      const binIndex = Math.min(Math.floor((value - min) / binWidth), numBins - 1);
      bins[binIndex].count++;
    });

    return bins;
  }, [rawData, config.numBins]);

  // Get color — use first palette color for locked themes, otherwise use config
  const barColor = getColors(config, 1, theme)[0];

  // ─── Tooltip ───────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  // Calculate Y domain
  const yDomain = useMemo(() => {
    const maxCount = Math.max(...histogramData.map(d => d.count));
    return niceDomain([0, maxCount], { includeZero: true, padding: 0.1, minOverride: config.yMin, maxOverride: config.yMax });
  }, [histogramData, config.yMin, config.yMax]);
  const yTicks = useMemo(
    () => scaleLinear({ domain: yDomain, range: [100, 0] }).ticks(),
    [yDomain],
  );
  const spacingSpec = getAxisSpacingSpec(theme);
  const { xTickGap, yTickGap, yTickBaselineNudge, xTickLength, yTickLength } = spacingSpec;
  const histLabels = useMemo(() => histogramData.map((d) => d.label), [histogramData]);
  const xAxis = useMemo(
    () => resolveXAxisLabels({
      labels: histLabels,
      chartWidth: width,
      chartHeight: height,
      baseFontSize: theme.tickSize ?? 13,
      fontFamily: theme.fontBody,
      rotationOverride: config.labelRotation,
      truncateLabels: !!config.truncateXAxisLabels,
    }),
    [histLabels, width, height, theme.tickSize, theme.fontBody, config.labelRotation, config.truncateXAxisLabels],
  );
  const xLanes = useMemo(() => computeAxisLanes({
    axisSide: 'bottom',
    labels: histLabels.map((l) => xAxis.tickFormat(l)),
    rotation: xAxis.rotation || 0,
    tickFontSize: xAxis.fontSize,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    tickLength: xTickLength,
    spacingSpec: { ...spacingSpec, gapX: xTickGap },
    telemetryMeta: { chartId: 'histogram', viewportWidth: width, viewportHeight: height },
  }), [histLabels, xAxis, theme.fontBody, theme.labelSize, xTickLength, spacingSpec, xTickGap, width, height]);
  const { margin, innerWidth, innerHeight } = computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    xLabels: histogramData.map(d => d.label),
    yTickValues: yTicks,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: 'vertical',
    baseRight: 80,
    baseBottom: 70,
    xAxisResolved: xAxis,
  });
  const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;

  // Scales
  const xScale = useMemo(() => scaleBand({
    domain: histogramData.map(d => d.label),
    range: [0, innerWidth],
    padding: 0, // no gap between bins (histogram)
  }), [histogramData, innerWidth]);

  const yScale = useMemo(() => scaleLinear({
    domain: yDomain,
    range: [innerHeight, 0],
  }), [yDomain, innerHeight]);
  const yLanes = useMemo(() => computeAxisLanes({
    axisSide: 'left',
    labels: yTicks.map((v) => String(v)),
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    tickLength: yTickLength,
    availableMargin: margin.left,
    spacingSpec: { ...spacingSpec, gapY: yTickGap },
    telemetryMeta: { chartId: 'histogram', viewportWidth: width, viewportHeight: height },
  }), [yTicks, theme.tickSize, theme.fontBody, theme.labelSize, yTickLength, margin.left, spacingSpec, yTickGap, width, height]);
  const axisDebugEnabled = isAxisDebugEnabled();
  if (axisDebugEnabled) {
    reportAxisLaneClamp(xLanes);
    reportAxisLaneClamp(yLanes);
  }

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          {/* Grid (horizontal only) */}
          {config.showGrid && (
            <GridRows
              scale={yScale}
              width={innerWidth}
              strokeDasharray="3 3"
              stroke={theme.gridColor}
              strokeOpacity={theme.gridAlpha}
            />
          )}

          {/* Bars */}
          {histogramData.map((bin) => {
            const barX = xScale(bin.label) ?? 0;
            const barWidth = xScale.bandwidth();
            const barY = yScale(bin.count);
            const barHeight = innerHeight - barY;
            if (barHeight <= 0) return null;
            return (
              <Bar
                key={bin.label}
                x={barX}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={barColor}
                stroke={config.showEdges ? theme.axisColor : 'none'}
                strokeWidth={config.showEdges ? 1 : 0}
                style={{ cursor: 'pointer' }}
                onMouseMove={(e) => showTip(e, String(bin.count))}
              />
            );
          })}

          {/* X Axis */}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={xTickLength}
            tickValues={xAxis.tickValues}
            tickFormat={xAxis.tickFormat}
            tickComponent={makeTickComponent(xAxis.rotation, { fontSize: xAxis.fontSize, fill: theme.tickColor, fontFamily: theme.fontBody })}
            tickLabelProps={() => ({ opacity: 0 })}
          />

          {/* Y Axis */}
          <AxisLeft
            scale={yScale}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={yTickLength}
            tickFormat={getCompactTickFormatter(yScale.domain()[1])}
            tickLabelProps={() => ({
              fill: theme.tickColor,
              fontSize: theme.tickSize ?? 13,
              textAnchor: 'end',
              dx: -yTickGap,
              dy: yTickBaselineNudge,
            })}
          />

          {/* Axis labels — manual rendering for consistent spacing */}
          <text
            x={-innerHeight / 2}
            y={-yLanes.titleOffsetPx}
            transform="rotate(-90)"
            textAnchor="middle"
            fontSize={theme.labelSize ?? 15}
            fill={theme.labelColor}
          >
            {config.ylabel || ''}
          </text>
          <text
            x={innerWidth / 2}
            y={xAxisTitleY}
            textAnchor="middle"
            fontSize={theme.labelSize ?? 15}
            fill={theme.labelColor}
          >
            {config.xlabel || ''}
          </text>
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                  titleOffsetPx: yLanes.titleOffsetPx,
                  clamped: yLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}
        </Group>
      </svg>
      <Tip />
    </div>
  );
}

export default HistogramPreview;
